import React, { useEffect, useState } from 'react';
import { FlipbaseConfig } from '../types/flipbase';

declare global {
  interface Window {
    Flipbase: any;
  }
}

interface FlipbaseRecorderProps {
  config: FlipbaseConfig;
  onVideoRecorded?: (videoId: string) => void;
}

export const FlipbaseRecorder: React.FC<FlipbaseRecorderProps> = ({ config, onVideoRecorded }) => {
  const [videoId, setVideoId] = useState<string | null>(localStorage.getItem('flipbase_video_id'));
  const [recorderVersion, setRecorderVersion] = useState<'v1' | 'v2'>('v2');
  const [formKey, setFormKey] = useState(0);
  const [isInitializing, setIsInitializing] = useState(false);
  const recorderRef = React.useRef<any>(null);
  const containerRef = React.useRef<HTMLDivElement>(null);
  
  const cleanupEverything = React.useCallback(() => {
    console.log('Cleaning up recorder...');
    
    // Destroy recorder instance
    if (recorderRef.current && typeof recorderRef.current.destroy === 'function') {
      try {
        recorderRef.current.destroy();
      } catch (error) {
        console.error('Error destroying recorder:', error);
      }
      recorderRef.current = null;
    }
    
    // Remove ALL recorder scripts (both v1 and v2)
    const allRecorderScripts = document.querySelectorAll('script[data-recorder-version]');
    allRecorderScripts.forEach(script => {
      try {
        script.remove();
        console.log('Removed recorder script:', script.getAttribute('src'));
      } catch (e) {
        console.error('Error removing script:', e);
      }
    });
    
    // Remove ALL recorder-related DOM elements that are OUTSIDE our container
    // This prevents conflicts with React's DOM management
    const recorderElements = document.querySelectorAll('[class*="recorder"], [id*="recorder"], [class*="flipbase-recorder"], .flipbase-input-container');
    recorderElements.forEach(el => {
      // Only remove elements that are NOT inside our React-managed container
      if (containerRef.current && !containerRef.current.contains(el)) {
        try {
          el.remove();
          console.log('Removed recorder element:', el.className || el.id);
        } catch (e) {
          console.error('Error removing element:', e);
        }
      }
    });
    
    // Remove ALL recorder-specific CSS and styles
    const cssLinks = document.querySelectorAll('link[href*="recorder"], style[data-recorder], style[id*="flipbase"]');
    cssLinks.forEach(link => {
      try {
        link.remove();
        console.log('Removed CSS/style:', link.getAttribute('href') || 'inline style');
      } catch (e) {
        console.error('Error removing CSS:', e);
      }
    });
    
    // Clear the Flipbase global object completely for a fresh start
    if (window.Flipbase) {
      try {
        delete window.Flipbase;
        console.log('Cleared window.Flipbase');
      } catch (e) {
        console.error('Error clearing Flipbase global:', e);
      }
    }
    
    console.log('Recorder cleanup complete - full refresh');
  }, []);
  
  useEffect(() => {
    // Skip initialization if no recorderId is provided
    if (!config.recorderId) {
      console.error('recorder_id is required');
      return;
    }

    // Prevent multiple simultaneous initializations
    if (isInitializing) {
      console.log('Already initializing, skipping...');
      return;
    }

    let isDestroyed = false;
    setIsInitializing(true);
    
    const initializeRecorder = async () => {
      console.log(`Initializing recorder ${recorderVersion}...`);
      
      // Don't initialize if we're already destroyed
      if (isDestroyed) {
        setIsInitializing(false);
        return;
      }
      
      // Complete cleanup
      cleanupEverything();
      
      // Force React to remount the form by updating the key
      setFormKey(prev => prev + 1);
      
      // Wait longer for cleanup to settle and ensure DOM is fully cleared
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (isDestroyed) {
        setIsInitializing(false);
        return;
      }
      
      // Determine which URL to use
      const recorderUrl = recorderVersion === 'v1' 
        ? (process.env.REACT_APP_RECORDER_URL_V1 || 'https://s3.eu-central-1.amazonaws.com/cdn.staging.flipbase.com/recorder/recorder.js')
        : (process.env.REACT_APP_RECORDER_URL || 'https://s3.eu-central-1.amazonaws.com/cdn.staging.flipbase.com/recorder/v2/recorder.js');
      
      console.log('Loading script:', recorderUrl);
      
      // Load the correct script
      const script = document.createElement('script');
      script.src = recorderUrl;
      script.async = true;
      script.setAttribute('data-recorder-version', recorderVersion);
      
      script.onload = () => {
        console.log('Script loaded, window.Flipbase:', !!window.Flipbase);
        
        if (!isDestroyed && window.Flipbase) {
          try {
            recorderRef.current = window.Flipbase.recorder({
              recorderId: config.recorderId,
              callback: function (id: string) {
                if (!isDestroyed) {  
                  console.log('Video recorded! ID:', id);
                  localStorage.setItem('flipbase_video_id', id);
                  setVideoId(id);
                  // Notify parent component to update the video ID input
                  if (onVideoRecorded) {
                    onVideoRecorded(id);
                  }
                }
              }
            });
            console.log('Recorder initialized successfully');
          } catch (error) {
            console.error('Error initializing recorder:', error);
          }
        }
        setIsInitializing(false);
      };

      script.onerror = () => {
        console.error(`Failed to load recorder script: ${recorderUrl}`);
        setIsInitializing(false);
      };

      document.head.appendChild(script);
    };

    initializeRecorder();

    return () => {
      console.log('Component cleanup triggered');
      isDestroyed = true;
      setIsInitializing(false);
      cleanupEverything();
    };
  }, [config.recorderId, recorderVersion, cleanupEverything, onVideoRecorded]);

  return (
    <div data-testid="flipbase-recorder" ref={containerRef}>
      <div style={{ marginBottom: '15px' }}>
        <button
          type="button"
          onClick={() => setRecorderVersion('v1')}
          disabled={isInitializing}
          style={{
            padding: '8px 16px',
            marginRight: '10px',
            backgroundColor: recorderVersion === 'v1' ? '#007bff' : '#e0e0e0',
            color: recorderVersion === 'v1' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: isInitializing ? 'not-allowed' : 'pointer',
            fontWeight: recorderVersion === 'v1' ? 'bold' : 'normal',
            opacity: isInitializing ? 0.6 : 1
          }}
        >
          Recorder V1
        </button>
        <button
          type="button"
          onClick={() => setRecorderVersion('v2')}
          disabled={isInitializing}
          style={{
            padding: '8px 16px',
            backgroundColor: recorderVersion === 'v2' ? '#007bff' : '#e0e0e0',
            color: recorderVersion === 'v2' ? 'white' : 'black',
            border: 'none',
            borderRadius: '4px',
            cursor: isInitializing ? 'not-allowed' : 'pointer',
            fontWeight: recorderVersion === 'v2' ? 'bold' : 'normal',
            opacity: isInitializing ? 0.6 : 1
          }}
        >
          Recorder V2
        </button>
      </div>
      <form key={formKey}>
        <input type="flipbase" data-testid="flipbase-recorder-widget" />
      </form>
      <div style={{ marginTop: '10px' }}>
        {videoId ? (
          <div 
            style={{ 
              padding: '10px',
              backgroundColor: '#f0f0f0',
              borderRadius: '4px',
              marginBottom: '10px'
            }}
            data-testid="flipbase-video-id-display"
          >
            <strong>Last recorded video ID:</strong> <span data-testid="flipbase-video-id-value">{videoId}</span>
          </div>
        ) : (
          <small>Record a video to get the video ID</small>
        )}
      </div>
    </div>
  );
};
